import { isEmpty, isNil } from 'lodash-es';
import uuidv4 from 'uuid/v4';

import { getCurrentDate } from 'site-modules/shared/utils/date-utils';
import { formatFinancingAttributes } from 'client/site-modules/shared/components/profile/attributes/financing';
import {
  collectAppraisalHistory,
  collectVehicles,
  getAppraisalRef,
  collectAppraisalOffersHistory,
  getTotalPrices,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import {
  getAllTmvConditionPricing,
  getAllConditionTotalPrices,
} from 'site-modules/shared/utils/appraisal/get-condition-pricing';
import { TRADE_IN_VALUE } from 'site-modules/shared/constants/appraisal/appraisal';

/**
 * Sets vehicleId if style has changed since last save.
 * @param {Object} vehicleIdRef - ref object for vehicleId
 * @param {Object} lastSavedStyleIdRef - ref object for lastSavedStyleId
 * @param {number|undefined} styleId - current style id
 */
export function setVehicleId(vehicleIdRef, lastSavedStyleIdRef, styleId) {
  if (styleId && lastSavedStyleIdRef.current !== styleId) {
    // TODO: Look into this no-param-reassign lint error. We are intentionally using refs to persist these values across renders without causing re-renders, and refs are mutable by design.
    lastSavedStyleIdRef.current = styleId; // eslint-disable-line no-param-reassign
    vehicleIdRef.current = uuidv4(); // eslint-disable-line no-param-reassign
  }
}

/**
 * Generates idmDataToSave(payload) to save into user profile.
 * @param {Object} allConditionTotalPrices
 * @param {Boolean} includePii
 * @param {number|null} offerPrice
 * @param {Object} state
 * @param {string} vehicleId
 * @param {Object} props
 * @returns {Object}
 */
export function generateIdmDataToSave({ allConditionTotalPrices, includePii, offerPrice, state, vehicleId, props }) {
  const { isAuthenticated } = props;
  const { payoffAmount = 0, condition } = state;
  const totalPrices = getTotalPrices(allConditionTotalPrices, condition);
  const tradeInValuationData = isEmpty(totalPrices)
    ? {}
    : formatFinancingAttributes({ tradeIn: totalPrices[TRADE_IN_VALUE], tradeInOwedAmount: payoffAmount });

  const idmDataToSave = {
    ...collectAppraisalHistory(state, vehicleId, totalPrices, allConditionTotalPrices, offerPrice),
    ...(isAuthenticated ? {} : { isAnonymous: true }),
    ...tradeInValuationData,
  };

  if (includePii) idmDataToSave.pii = collectVehicles(state, vehicleId);

  return idmDataToSave;
}

/**
 * Get user profile appraisalMultiOffers and appraisalMultiOffersHistory.
 * @param {Object} multiOfferData
 * @param {string} vehicleId
 * @param {boolean} isAuthenticated
 * @returns {Object|undefined}
 */
export function getMultiOfferDataToIdm(multiOfferData, vehicleId, isAuthenticated) {
  if (isAuthenticated !== null) {
    const appraisalRef = getAppraisalRef(vehicleId);
    const appraisalMultiOffers = {
      ...multiOfferData,
      appraisalRef,
    };
    return {
      appraisalMultiOffersHistory: [appraisalMultiOffers],
      appraisalMultiOffers,
    };
  }
  return undefined;
}

/**
 * Get user profile appraisalOffers and merged attributes.
 * @param {Object} instantOfferData
 * @param {string} vehicleId
 * @param {Object} state
 * @param {Object} props
 * @param {Function} generateIdmDataToSaveFn
 * @returns {Object|undefined}
 */
export function getInstantOfferDataToIdm({ instantOfferData, vehicleId, state, props, generateIdmDataToSaveFn }) {
  const INCLUDE_PII_FALSE = false;
  const {
    isAuthenticated,
    tmvConditionsResponseData: { tmvConditions },
  } = props;
  const { options } = state;

  if (isAuthenticated !== null) {
    const appraisalRef = getAppraisalRef(vehicleId);
    const appraisalOffers = {
      ...instantOfferData,
      appraisalRef,
    };
    let idmDataToSave = {};
    if (!isNil(tmvConditions)) {
      const { offerPrice } = instantOfferData;
      const allConditionPricing = getAllTmvConditionPricing(tmvConditions, options);
      const allConditionTotalPrices = getAllConditionTotalPrices(allConditionPricing);
      idmDataToSave = generateIdmDataToSaveFn(allConditionTotalPrices, INCLUDE_PII_FALSE, offerPrice);
    }
    return {
      ...idmDataToSave,
      ...collectAppraisalOffersHistory(appraisalOffers),
      appraisalOffers,
      leads: {
        [vehicleId]: {
          type: 'Appraisal',
          submittedTs: getCurrentDate().toISOString(),
          appraisalRef,
        },
      },
    };
  }
  return undefined;
}
